/**
 * Good to Start for one-click: the duration a press will use, on the armed
 * strip (dashboard toolbar cell: beside the hotkey target readout; Game Detail
 * header: under it), where it widens neither host.
 *
 * One fixed-width face for every value ("GTS", "GTC", "GTD 7:45p", or the
 * refused mode with a cross), so a change of duration, arming, or kickoff
 * changes text inside a box that never resizes. The duration is visible
 * text; the title is the quick reference only.
 *
 * Game Detail resolves this page's game exactly as a press would (the
 * ticket's Kalshi selection, the event start, live or not), and re-reads the
 * clock each second while armed so the face turns GTC at kickoff. The
 * dashboard has no single game: it shows the Settings default, and the title
 * states the per-game rule a press applies.
 *
 * node1's single-game hotkey screens (the /live game view and the Sniper
 * cockpit) have no order ticket: their presses use the Settings default, so
 * the badge resolves their game against it, exactly as a press there would.
 */
import { cn } from '@/lib/infra/utils';
import { useSharedNow } from '@/lib/infra/sharedClock';
import { useGrantSlot } from '@/hooks/useGrantSlot';
import { useDisplaySettings } from '@/stores/displaySettings';
import { formatStartTimeLocal, useMarketStore } from '@/stores/marketStore';
import { useOneClickStore } from '@/stores/oneClickStore';
import {
  durationBadgeText,
  oneClickExpirySelection,
  oneClickGameClock,
  resolveOneClickDuration,
  useOneClickTicketExpiry,
  type OneClickDuration,
  type OneClickExpirySelection,
} from '@/lib/trading/oneClickExpiry';

export type OneClickDurationScope =
  | { kind: 'dashboard' }
  /** `ticker`: any Kalshi market of the page's game; null = none known. */
  | { kind: 'game-detail'; ticker: string | null }
  /** A single-game screen without a ticket (/live, Sniper): the Settings
   *  default, resolved against this game. */
  | { kind: 'live-game'; ticker: string | null };

interface Face {
  text: string;
  title: string;
  tone: 'bounded' | 'open' | 'refused';
}

const SOURCE_NAME: Record<OneClickExpirySelection['source'], string> = {
  ticket: 'the ticket',
  settings: 'your Settings default',
};

/** The dashboard face: the selection only, with the per-game rule. */
function selectionFace(selection: OneClickExpirySelection): Face {
  const from = SOURCE_NAME[selection.source];
  if (selection.mode === 'gtc') {
    return {
      text: 'GTC',
      tone: 'open',
      title: `One-click duration: Good to Close (${from}). Orders rest until cancelled or the market settles.`,
    };
  }
  if (selection.mode === 'gts') {
    return {
      text: 'GTS',
      tone: 'bounded',
      title:
        `One-click duration: Good to Start (${from}). Before a game starts, a press expires at that game's start, fixed when placed; ` +
        'with no known future start the press is refused. Live games: Good to Close.',
    };
  }
  return {
    text: 'GTD ✗',
    tone: 'refused',
    title:
      `One-click duration: Custom (${from}), which has a time only on the Game Detail ticket, so pregame presses here are refused. ` +
      'Live games: Good to Close.',
  };
}

/** The Game Detail face: what a press on this game does right now. */
function durationFace(duration: OneClickDuration, selection: OneClickExpirySelection): Face {
  const text = durationBadgeText(duration);
  const from = SOURCE_NAME[selection.source];
  if (!duration.ok) return { text, tone: 'refused', title: duration.message };
  if (duration.kind === 'gtc') {
    return {
      text,
      tone: 'open',
      title:
        duration.reason === 'live'
          ? 'This game is live: one-click orders rest Good to Close.'
          : `One-click duration: Good to Close (${from}). Orders rest until cancelled or the market settles.`,
    };
  }
  const at = formatStartTimeLocal(duration.expirationMs);
  return {
    text,
    tone: 'bounded',
    title:
      duration.kind === 'gts'
        ? `One-click duration: Good to Start (${from}). A press now expires at ${at}, the event start as it stands when placed; a later reschedule does not move it.`
        : `One-click duration: Custom (${from}). A press now expires at ${at}.`,
  };
}

const TONE_CLASS: Record<Face['tone'], string> = {
  bounded: 'border-border text-foreground',
  open: 'border-border/50 text-muted-foreground',
  refused: 'border-negative/60 text-negative',
};

export function OneClickDurationBadge({ scope }: { scope: OneClickDurationScope }) {
  const slot = useGrantSlot('oneClickAccess');
  const armed = useOneClickStore((s) => s.armed);
  const ticket = useOneClickTicketExpiry((s) => s.ticket);
  const defaultMode = useDisplaySettings((s) => s.defaultExpiryMode);
  // Re-resolve on market frames (a start or status arriving) and, while
  // armed, each second, so the face turns GTC at kickoff on its own.
  const version = useMarketStore((s) => s.version);
  const gameTicker = scope.kind === 'dashboard' ? null : scope.ticker;
  const nowMs = useSharedNow(1000, armed && gameTicker != null);
  if (slot === 'hidden') return null;

  void version;
  const selection = oneClickExpirySelection(
    scope.kind === 'game-detail' ? 'gd-moneyline' : scope.kind === 'live-game' ? 'live-game' : 'dash-moneyline',
    ticket,
    defaultMode,
  );
  const face =
    gameTicker != null
      ? durationFace(
          resolveOneClickDuration({ selection, ...oneClickGameClock(gameTicker, nowMs), nowMs }),
          selection,
        )
      : selectionFace(selection);

  return (
    <span
      data-testid="one-click-duration"
      aria-label={`One-click duration ${face.text}`}
      title={face.title}
      className={cn(
        // Fixed slot: wide enough for "GTD 12:45p"; every face is centred in
        // it, so nothing beside it moves when the duration changes.
        'inline-flex w-[5.5rem] shrink-0 items-center justify-center whitespace-nowrap rounded-lg border px-1.5 py-1 font-mono text-xs font-semibold',
        TONE_CLASS[face.tone],
        !armed && 'opacity-60',
      )}
    >
      {face.text}
    </span>
  );
}
